"use client";

import { FormEvent, useState, useMemo, useCallback, useEffect } from "react";
import { Ticket, ListOrdered, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSession } from "@/lib/auth/client";
import type { DropEntryMode } from "@/types/drop";
import { getEntryWindowState } from "@/lib/commerce/drop-entry-window";

type ViewState = "entry" | "pending" | "waitlist" | "closed" | "not_started";

interface DropInteractionProps {
  dropId: number;
  mode: DropEntryMode;
  entryStartsAt?: string | null;
  entryEndsAt?: string | null;
  entryOpenEnabled?: boolean;
}

function EmailField({
  dropId,
  prefix,
  email,
  setEmail,
  authedEmail,
}: {
  dropId: number;
  prefix: string;
  email: string;
  setEmail: (v: string) => void;
  authedEmail: string;
}) {
  const isAuthed = Boolean(authedEmail);
  if (isAuthed && email !== "") {
    return (
      <div className="border-foreground/15 bg-foreground/[0.03] flex items-center justify-between gap-2 border px-3 py-2.5">
        <span className="font-mono text-xs">
          <span className="text-muted-foreground">Signed in ·</span> <span className="font-bold">{authedEmail}</span>
        </span>
        <button
          type="button"
          onClick={() => setEmail("")}
          className="text-muted-foreground hover:text-foreground text-[10px] font-bold tracking-widest uppercase underline decoration-dotted underline-offset-4"
        >
          Change
        </button>
      </div>
    );
  }
  return (
    <Input
      id={`${prefix}-${dropId}`}
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="you@example.com"
      required={!isAuthed}
      autoComplete="email"
      className="border-foreground/15 bg-background h-11 rounded-none font-mono text-sm"
    />
  );
}

import EntryShell from "@/components/drop/EntryShell";
import DropCountdown from "@/components/drop/DropCountdown";

export default function DropInteraction({ dropId, mode, entryStartsAt, entryEndsAt, entryOpenEnabled = true }: DropInteractionProps) {
  const { data: session } = useSession();
  const authedEmail = session?.user?.email ?? "";
  const isAuthed = Boolean(authedEmail);
  const isRaffle = mode === "raffle";

  const windowState = useMemo(
    () => getEntryWindowState(entryStartsAt ?? null, entryEndsAt ?? null),
    [entryStartsAt, entryEndsAt]
  );

  const initialView: ViewState = useMemo(() => {
    if (windowState === "not_started") return "not_started";
    if (windowState === "closed") return "closed";
    return "entry";
  }, [windowState]);

  const [view, setView] = useState<ViewState>(initialView);
  const [email, setEmail] = useState(() => authedEmail);
  const [pending, setPending] = useState(false);
  const [notifyPending, setNotifyPending] = useState(false);

  const [prevWindow, setPrevWindow] = useState(windowState);
  if (prevWindow !== windowState && view !== "pending" && view !== "waitlist") {
    setPrevWindow(windowState);
    if (windowState === "not_started") setView("not_started");
    else if (windowState === "closed") setView("closed");
    else setView("entry");
  }
  const [prevAuthed, setPrevAuthed] = useState(authedEmail);
  if (prevAuthed !== authedEmail && !email && authedEmail) {
    setPrevAuthed(authedEmail);
    setEmail(authedEmail);
  } else if (prevAuthed !== authedEmail) {
    setPrevAuthed(authedEmail);
  }

  const getBrowserId = useCallback(() => {
    if (typeof window === "undefined" || isAuthed) return null as string | null;
    try {
      const existing = localStorage.getItem("browser_id");
      if (existing) {
        document.cookie = `browser_id=${encodeURIComponent(existing)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
        return existing;
      }
      const id = `br_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      localStorage.setItem("browser_id", id);
      document.cookie = `browser_id=${encodeURIComponent(id)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
      return id;
    } catch { return null; }
  }, [isAuthed]);

  useEffect(() => {
    if (isAuthed) return;
    try {
      if (localStorage.getItem(`entry:drop:${dropId}`) === "1") setView("pending");
    } catch (err) { console.warn("localStorage read failed:", err); }
  }, [isAuthed, dropId]);

  const [myPosition, setMyPosition] = useState<number | null>(null);
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    fetch(`/api/drops/${dropId}/my-entry`).then((r) => r.json()).then((j) => {
      if (cancelled || !j?.entry) return;
      const st = String(j.entry.status);
      const pos = j.entry.position ?? null;
      if (pos) setMyPosition(Number(pos));
      if (st === "winner" || st === "offered" || st === "entered" || st === "claimed" || st === "queued") setView("pending");
      else if (st === "loser" || st === "waiting") setView("waitlist");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthed, dropId]);

  const handleEntry = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (windowState !== "open" && windowState !== "no_window") {
        toast.error(windowState === "not_started" ? "Entries have not opened yet." : "Entries are closed for this drop.");
        return;
      }
      const emailToSend = isAuthed && email !== "" ? authedEmail : email;
      if (!emailToSend) {
        toast.error("Enter an email address");
        return;
      }
      if (!isAuthed) {
        try {
          if (localStorage.getItem(`entry:drop:${dropId}`) === "1") {
            toast.error("Only one entry per browser — you’ve already entered.");
            setPending(false);
            setView("pending");
            return;
          }
        } catch (err) { console.warn("localStorage read failed:", err); }
      }
      setPending(true);
      try {
        const browserId = getBrowserId();
        const res = await fetch(`/api/drops/${dropId}/entries`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailToSend, browserId: browserId ?? undefined }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || "Could not join");
        try { if (!isAuthed) localStorage.setItem(`entry:drop:${dropId}`, "1"); } catch (err) { console.warn("localStorage write failed:", err); }
        toast.success(body.position ? `${body.message || "You're on the list!"} #${body.position}` : body.message || "You're on the list!");
        setView("pending");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not join");
      } finally {
        setPending(false);
      }
    },
    [windowState, isAuthed, authedEmail, email, dropId, getBrowserId]
  );

  const handleWaitlist = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const emailToSend = isAuthed && email !== "" ? authedEmail : email;
      if (!emailToSend) {
        toast.error("Enter an email address");
        return;
      }
      setPending(true);
      try {
        const res = await fetch(`/api/drops/${dropId}/waitlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailToSend }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || "Could not join waitlist");
        toast.success(body.message || "You're on the waitlist!");
        setView("waitlist");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not join waitlist");
      } finally {
        setPending(false);
      }
    },
    [isAuthed, authedEmail, email, dropId]
  );

  const handleNotify = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const emailToSend = isAuthed && email !== "" ? authedEmail : email;
      if (!emailToSend) {
        toast.error("Enter an email address");
        return;
      }
      setNotifyPending(true);
      try {
        const res = await fetch("/api/notifications", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailToSend, dropId, kind: "entry_open" }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || "Could not join notify list");
        toast.success(body.message || "We'll notify you when entries open.");
        setView("pending");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not join notify list");
      } finally {
        setNotifyPending(false);
      }
    },
    [isAuthed, authedEmail, email, dropId]
  );

  if (view === "pending") {
    const PendingIcon = windowState === "not_started" ? Bell : isRaffle ? Ticket : ListOrdered;
    return (
      <EntryShell mode={mode} state="pending">
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background">
            <PendingIcon className="h-4 w-4" aria-hidden />
          </span>
          <div className="space-y-1.5">
            <p className="text-sm font-black tracking-[0.14em] uppercase">
              {windowState === "not_started"
                ? "You’re on the notify list"
                : isRaffle
                  ? "You’re in the raffle"
                  : myPosition ? `Position #${myPosition} secured` : "Position secured"}
            </p>
            <p className="text-muted-foreground mx-auto max-w-sm text-xs leading-relaxed">
              {isRaffle ? "We’ll email you when winners are drawn." : myPosition ? `You’re #${myPosition} in line — we’ll email you when it’s your turn.` : "We’ll email you when it’s your turn."}
            </p>
          </div>
          {myPosition && !isRaffle && <p className="rounded-full border border-foreground/15 bg-muted px-3 py-1 font-mono text-[11px] font-bold tracking-widest uppercase text-foreground">Queue position #{myPosition}</p>}
        </div>
      </EntryShell>
    );
  }

  if (view === "waitlist") {
    return (
      <EntryShell mode={mode} state="waitlist">
        <div className="flex flex-col items-center gap-2 py-3 text-center">
          <Bell className="h-4 w-4" />
          <p className="text-xs font-black tracking-[0.16em] uppercase">You’re on the waitlist</p>
          <p className="text-muted-foreground text-xs">We’ll notify you if a spot opens. No further action needed.</p>
        </div>
      </EntryShell>
    );
  }

  if (view === "not_started") {
    if (!entryOpenEnabled) {
      const startsLabel = entryStartsAt ? new Date(entryStartsAt).toLocaleString() : null;
      return (
        <EntryShell mode={mode} state="not_started">
          <div className="flex flex-col gap-2 py-2 text-center">
            <h3 className="text-sm font-black uppercase tracking-tight">{isRaffle ? "Raffle opens soon" : "Queue opens soon"}</h3>
            <p className="text-muted-foreground text-xs leading-relaxed">{startsLabel ? `Entries open ${startsLabel}.` : "Entries haven’t opened yet."}</p>
            {entryStartsAt && <p className="mt-2 font-mono text-[11px] font-bold tracking-widest uppercase">Opens in <DropCountdown target={entryStartsAt} /></p>}
          </div>
        </EntryShell>
      );
    }
    const startsLabel = entryStartsAt ? new Date(entryStartsAt).toLocaleString() : null;
    return (
      <EntryShell mode={mode} state="not_started">
        <form onSubmit={handleNotify} className="flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-black uppercase tracking-tight">{isRaffle ? "Raffle opens soon" : "Queue opens soon"}</h3>
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
              {startsLabel ? `Entries open ${startsLabel}.` : "Entries haven’t opened yet."} Get a one-tap email the moment they do.
            </p>
            {entryStartsAt && (
              <p className="mt-2 font-mono text-[11px] font-bold tracking-widest uppercase text-foreground">
                Opens in <DropCountdown target={entryStartsAt} />
              </p>
            )}
          </div>
          <EmailField dropId={dropId} prefix="notify-entry" email={email} setEmail={setEmail} authedEmail={authedEmail} />
          <Button
            type="submit"
            disabled={notifyPending || (!isAuthed && !email)}
            className="h-11 rounded-none text-xs font-black tracking-[0.14em] uppercase"
          >
            <Bell data-icon="inline-start" />
            {notifyPending ? "Saving…" : isAuthed ? "Notify me — 1 click" : "Notify me"}
          </Button>
        </form>
      </EntryShell>
    );
  }

  if (view === "closed") {
    if (!entryOpenEnabled) {
      return (
        <EntryShell mode={mode} state="closed">
          <div className="flex flex-col gap-2 py-2 text-center">
            <h3 className="text-sm font-black uppercase tracking-tight">Entries closed</h3>
            <p className="text-muted-foreground text-xs leading-relaxed">Entries are closed.</p>
          </div>
        </EntryShell>
      );
    }
    if (isRaffle) {
      return (
        <EntryShell mode={mode} state="closed">
          <form onSubmit={handleWaitlist} className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-black uppercase tracking-tight">Entries closed — join the waitlist</h3>
              <p className="text-muted-foreground mt-1 text-xs leading-relaxed">Missed the window? One tap and we’ll pull you in if a winner declines.</p>
            </div>
            <EmailField dropId={dropId} prefix="waitlist" email={email} setEmail={setEmail} authedEmail={authedEmail} />
            <Button
              type="submit"
              disabled={pending || (!isAuthed && !email)}
              className="h-11 rounded-none text-xs font-black tracking-[0.14em] uppercase"
            >
              <Bell data-icon="inline-start" />
              {pending ? "Saving…" : isAuthed ? "Join waitlist — 1 click" : "Join Waitlist"}
            </Button>
          </form>
        </EntryShell>
      );
    }
    return (
      <EntryShell mode={mode} state="closed">
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-black uppercase tracking-tight">Queue closed</h3>
          <p className="text-muted-foreground text-xs leading-relaxed">We’re processing the queue. Leave your email for general sale.</p>
          <form onSubmit={handleNotify} className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <EmailField dropId={dropId} prefix="notify-closed" email={email} setEmail={setEmail} authedEmail={authedEmail} />
            </div>
            <Button type="submit" disabled={notifyPending || (!isAuthed && !email)} className="h-11 shrink-0 rounded-none px-5 text-xs font-black tracking-[0.14em] uppercase">
              <Bell data-icon="inline-start" />
              {notifyPending ? "Saving…" : "Notify me"}
            </Button>
          </form>
        </div>
      </EntryShell>
    );
  }

  return (
    <EntryShell mode={mode} state="entry">
      <form onSubmit={handleEntry} className="flex flex-col gap-4">
        <div>
          <h3 className="text-base font-black uppercase tracking-tighter">{isRaffle ? "Join the raffle" : "Join the queue"}</h3>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            {isRaffle ? "Random draw when the window closes." : "First-come, first-served. Your position is saved."}
            {isAuthed && <span className="text-foreground font-bold"> · One-click as {authedEmail}</span>}
          </p>
          {entryEndsAt && windowState === "open" && (
            <p className="mt-2 font-mono text-[11px] font-bold tracking-widest uppercase text-foreground">
              Closes in <DropCountdown target={entryEndsAt} />
            </p>
          )}
        </div>
        <EmailField dropId={dropId} prefix="entry" email={email} setEmail={setEmail} authedEmail={authedEmail} />
        <Button
          type="submit"
          disabled={pending || (!isAuthed && !email)}
          className="h-11 rounded-none text-xs font-black tracking-[0.14em] uppercase"
        >
          {isRaffle ? <Ticket data-icon="inline-start" /> : <ListOrdered data-icon="inline-start" />}
          {pending ? "Saving…" : isRaffle ? "Enter Raffle — 1 click" : "Join Queue — 1 click"}
        </Button>
        <p className="text-muted-foreground font-mono text-[10px] leading-relaxed">
          {isRaffle ? "Winners get a timed claim link by email." : "You’ll get a timed purchase window by email when it’s your turn."}
        </p>
      </form>
    </EntryShell>
  );
}
